import React, { useState, useEffect, useRef } from 'react';
import banner1 from '../../images/banner1.webp';
import banner2 from '../../images/banner2.webp';
import banner3 from '../../images/banner3.webp';
import banner6 from '../../images/banner6.webp';

const Banner = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const items = [banner1, banner2, banner3, banner6];
  const touchStartX = useRef(0);
  const intervalRef = useRef(null);

  // Function to start the interval
  const startInterval = () => {
    intervalRef.current = setInterval(() => {
      setActiveIndex((prevIndex) =>
        prevIndex === items.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change slide every 5 seconds
  };

  // Function to stop the interval
  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  useEffect(() => {
    startInterval();
    return () => stopInterval();
  }, []);

  useEffect(() => {
    const hideControlsTimeout = setTimeout(() => {
      setShowControls(false);
    }, 3000); // Hide controls after 3 seconds of inactivity

    return () => clearTimeout(hideControlsTimeout);
  }, [activeIndex]);

  const handleInteractionStart = (e) => {
    touchStartX.current = e.touches ? e.touches[0].clientX : e.clientX;
  };

  const handleInteractionEnd = (e) => {
    const touchEndX = e.changedTouches
      ? e.changedTouches[0].clientX
      : e.clientX;
    const touchDiff = touchStartX.current - touchEndX;
    if (touchDiff > 50) handleNext();
    if (touchDiff < -50) handlePrev();
  };

  const handlePrev = () => {
    stopInterval();
    setActiveIndex((prevIndex) =>
      prevIndex === 0 ? items.length - 1 : prevIndex - 1
    );
    startInterval();
    setShowControls(true);
  };

  const handleNext = () => {
    stopInterval();
    setActiveIndex((prevIndex) =>
      prevIndex === items.length - 1 ? 0 : prevIndex + 1
    );
    startInterval();
    setShowControls(true);
  };

  const preloadImages = () => {
    items.forEach((item) => {
      const img = new Image();
      img.src = item;
    });
  };

  useEffect(() => {
    preloadImages();
  }, []);

  // Reset the interval when the user manually changes the slide
  useEffect(() => {
    stopInterval();
    startInterval();
  }, [activeIndex]);

  return (
    <div
      id="indicators-carousel"
      className="relative w-full group"
      data-carousel="static"
      onTouchStart={handleInteractionStart}
      onTouchEnd={handleInteractionEnd}
      onMouseDown={handleInteractionStart}
      onMouseUp={handleInteractionEnd}
    >
      <div className="relative h-96 md:h-[550px] overflow-hidden carousel-container">
        {items.map((item, index) => (
          <div
            key={index}
            className={`absolute inset-0 duration-700 ease-in-out transition-transform transform ${
              index === activeIndex ? 'translate-x-0' : 'translate-x-full'
            }`}
            data-carousel-item={index === activeIndex ? 'active' : undefined}
          >
            <img
              src={item}
              className="w-full h-full object-cover carousel-image"
              alt={`Slide ${index + 1}`}
            />
            {index === 0 && (
              <div
                className={`absolute bottom-10 left-10 right-10 lg:left-1/4 lg:w-1/3 lg:transform lg:-translate-x-1/2 text-white ${
                  index === activeIndex ? 'animate-fade-in-delay' : ''
                } bg-gray-700 bg-opacity-30 p-4 rounded-2xl`}
              >
                <h1 className="text-4xl lg:text-6xl font-bold">
                  Unsere Praxis
                </h1>
                <p className="text-md lg:text-xl">
                  Willkommen In Unserer Zahnpraxis In Roseggerstraße 23, 5020
                  Salzburg
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="absolute z-30 flex -translate-x-1/2 space-x-3 rtl:space-x-reverse bottom-5 left-1/2">
        {items.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`w-3 h-3 rounded-full ${
              index === activeIndex ? 'bg-primary' : 'bg-gray-400'
            }`}
            aria-current={index === activeIndex ? 'true' : 'false'}
            aria-label={`Slide ${index + 1}`}
            onClick={() => {
              setActiveIndex(index);
              setShowControls(true);
            }}
          ></button>
        ))}
      </div>

      <button
        type="button"
        className={`absolute top-0 left-0 z-30 flex items-center justify-center h-full px-4 cursor-pointer transition-opacity duration-500 ${
          showControls ? 'opacity-100' : 'opacity-0'
        } group-hover:opacity-100 group-focus:opacity-100`}
        data-carousel-prev
        onClick={handlePrev}
      >
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/30 dark:bg-gray-800/30 group-hover:bg-white/50 dark:group-hover:bg-gray-800/60 group-focus:ring-4 group-focus:ring-white dark:group-focus:ring-gray-800/70 group-focus:outline-none">
          <svg
            className="w-4 h-4 text-white dark:text-gray-800 rtl:rotate-180"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 6 10"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 1 1 5l4 4"
            />
          </svg>
          <span className="sr-only">Previous</span>
        </span>
      </button>

      <button
        type="button"
        className={`absolute top-0 right-0 z-30 flex items-center justify-center h-full px-4 cursor-pointer transition-opacity duration-500 ${
          showControls ? 'opacity-100' : 'opacity-0'
        } group-hover:opacity-100 group-focus:opacity-100`}
        data-carousel-next
        onClick={handleNext}
      >
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/30 dark:bg-gray-800/30 group-hover:bg-white/50 dark:group-hover:bg-gray-800/60 group-focus:ring-4 group-focus:ring-white dark:group-focus:ring-gray-800/70 group-focus:outline-none">
          <svg
            className="w-4 h-4 text-white dark:text-gray-800 rtl:rotate-180"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 6 10"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m1 9 4-4-4-4"
            />
          </svg>
          <span className="sr-only">Next</span>
        </span>
      </button>
    </div>
  );
};

export default Banner;
